import { DEFAULT_DATE_FORMAT, formatDateValue } from "../utils/dateUtils";
import {
  filterValueMultiplier,
  subTypeValueAffixes,
} from "../utils/numberUtils";
import {
  buildGroupingAggregateEntry,
  buildMatchGroupingAggregateEntry,
  canInsertDialogRow,
  createDialogRows,
  deleteDialogRow,
  insertDialogRow,
  isNullTargetRow,
  normaliseLabelOverride,
  nullTargetAfterDelete,
  nullTargetFromRowIndex,
  nullTargetRowIndex,
  rowNullTarget,
  setRowLabelOverride,
  subTypeThresholdFormatter,
  updateDialogRow,
  validateDialogRows,
} from "../utils/rangeGroupingUtils";
import RangeGroupingMatchHelp from "./RangeGroupingMatchHelp";
import RangeGroupingNoValueEntry from "./RangeGroupingNoValueEntry";
import RangeGroupingRow from "./RangeGroupingRow";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "components/common/Button";
import {
  RANGE_ROW_ERROR_NOT_A_DATE,
  RANGE_ROW_ERROR_NOT_A_NUMBER,
  RANGE_NULL_TARGET_NO_VALUE,
  RANGE_ROW_ERROR_NOT_ASCENDING,
  RANGE_VALUE_ENTRY_ATTRIBUTE,
  RANGE_VALUE_TYPE_DATE,
  RANGE_VALUE_TYPE_NUMBER,
  RANGE_VALUE_TYPE_STRING,
} from "components/reportsV3/types/constants";
import * as labels from "config.labels";
import React, { useEffect, useRef, useState } from "react";
import { useConfigQuery } from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const RangeGroupingDialog = ({
  open,
  sourceColumn,
  sourceDisplayName,
  initialRanges,
  initialNoValue,
  valueType = RANGE_VALUE_TYPE_NUMBER,
  subType = null,
  onSave,
  onClose,
}) => {
  const nextId = useRef(0);
  const makeId = () => nextId.current++;
  const [rows, setRows] = useState(() => createDialogRows([], makeId));
  const [focusId, setFocusId] = useState(null);
  const contentRef = useRef(null);
  const [nullTarget, setNullTarget] = useState(RANGE_NULL_TARGET_NO_VALUE);
  const [noValueLabelOverride, setNoValueLabelOverride] = useState(null);
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const { data: dateFormatConfig } = useConfigQuery("date_format");
  const dateFormat = dateFormatConfig?.date_format || DEFAULT_DATE_FORMAT;
  const isDate = valueType === RANGE_VALUE_TYPE_DATE;
  const isString = valueType === RANGE_VALUE_TYPE_STRING;
  const numberSubType = valueType === RANGE_VALUE_TYPE_NUMBER ? subType : null;
  const { prefix: valuePrefix, suffix: valueSuffix } = subTypeValueAffixes(
    numberSubType
  );

  useEffect(() => {
    if (open) {
      const initialRows = createDialogRows(initialRanges, makeId);
      setRows(initialRows);
      setNullTarget(
        isString
          ? nullTargetFromRowIndex(initialRows, initialNoValue?.nullTarget)
          : initialNoValue?.nullTarget ?? RANGE_NULL_TARGET_NO_VALUE
      );
      setNoValueLabelOverride(initialNoValue?.labelOverride ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const templates = {
    lessThan: translateLabels(labels.RANGE_GROUPING_LABEL_LESS_THAN, {
      default: "Less than {to}",
    }),
    fromLessThan: translateLabels(labels.RANGE_GROUPING_LABEL_FROM_LESS_THAN, {
      default: "{from} and less than {to}",
    }),
    andAbove: translateLabels(labels.RANGE_GROUPING_LABEL_AND_ABOVE, {
      default: "{from} and above",
    }),
    noValue: translateLabels(labels.RANGE_GROUPING_LABEL_NO_VALUE, {
      default: "No Value",
    }),
    matches: translateLabels(labels.RANGE_GROUPING_LABEL_MATCHES, {
      default: "Matches {pattern}",
    }),
    theRest: translateLabels(labels.RANGE_GROUPING_LABEL_THE_REST, {
      default: "The rest",
    }),
  };
  const formatValue = isDate
    ? (value) => formatDateValue(value, dateFormat)
    : subTypeThresholdFormatter(numberSubType);

  const {
    errors,
    thresholds,
    defaultLabels,
    labels: rowLabels,
    duplicates,
    canSave,
    noValueDefaultLabel,
    noValueLabel,
    noValueDuplicate,
  } = validateDialogRows(rows, valueType, templates, formatValue, {
    nullTarget,
    labelOverride: noValueLabelOverride,
  });

  const errorTexts = {
    [RANGE_ROW_ERROR_NOT_A_NUMBER]: translateLabels(
      labels.RANGE_GROUPING_DIALOG_ERROR_NOT_A_NUMBER,
      { default: "Must be a number" }
    ),
    [RANGE_ROW_ERROR_NOT_A_DATE]: translateLabels(
      labels.RANGE_GROUPING_DIALOG_ERROR_NOT_A_DATE,
      { default: "Must be a date" }
    ),
    [RANGE_ROW_ERROR_NOT_ASCENDING]: translateLabels(
      labels.RANGE_GROUPING_DIALOG_ERROR_NOT_ASCENDING,
      { default: "Must be greater than the line above" }
    ),
  };

  const handleChange = (index, text) => {
    const newId = makeId();
    setRows((current) =>
      updateDialogRow(current, index, text, valueType, newId)
    );
  };

  const handleDelete = (index) => {
    if (isString) {
      setNullTarget((current) => nullTargetAfterDelete(rows, index, current));
    }
    setRows((current) => deleteDialogRow(current, index));
  };

  const handleInsert = (index) => {
    const newId = makeId();
    setFocusId(newId);
    setRows((current) => insertDialogRow(current, index, newId));
  };

  const handleValueTab = (index) => {
    if (!rows[index]?.text?.trim()) return;
    const entries = contentRef.current?.querySelectorAll(
      `[${RANGE_VALUE_ENTRY_ATTRIBUTE}]`
    );
    entries?.[index + 1]?.querySelector("input")?.focus();
  };

  const handleLabelChange = (index, text) =>
    setRows((current) => setRowLabelOverride(current, index, text));

  const handleSave = () => {
    const displayName = translateLabels(
      labels.RANGE_GROUPING_COLUMN_DISPLAY_NAME,
      {
        default: "Group by {name}",
        replacements: { name: sourceDisplayName },
      }
    );
    const labelOverrides = rows.map((row) => row.labelOverride);
    const noValueOverride =
      nullTarget === RANGE_NULL_TARGET_NO_VALUE ? noValueLabelOverride : null;
    onSave(
      isString
        ? buildMatchGroupingAggregateEntry({
            sourceColumn,
            displayName,
            patterns: thresholds,
            templates,
            labelOverrides,
            nullTargetIndex: nullTargetRowIndex(rows, nullTarget),
            noValueLabelOverride: noValueOverride,
          })
        : buildGroupingAggregateEntry({
            sourceColumn,
            displayName,
            thresholds,
            templates,
            formatValue,
            labelOverrides,
            nullTarget,
            noValueLabelOverride: noValueOverride,
            multiplier: filterValueMultiplier(numberSubType),
          })
    );
    onClose();
  };

  const handleClear = () => {
    setRows(createDialogRows([], makeId));
    setNullTarget(RANGE_NULL_TARGET_NO_VALUE);
    setNoValueLabelOverride(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
      <DialogTitle>
        {translateLabels(labels.RANGE_GROUPING_DIALOG_TITLE, {
          default: "Group by {name}",
          replacements: { name: sourceDisplayName },
        })}
      </DialogTitle>
      <DialogContent ref={contentRef}>
        <RangeGroupingNoValueEntry
          labelText={noValueLabel}
          defaultLabel={noValueDefaultLabel}
          labelOverride={noValueLabelOverride}
          active={nullTarget === RANGE_NULL_TARGET_NO_VALUE}
          isDuplicate={noValueDuplicate}
          onLabelChange={(text) =>
            setNoValueLabelOverride(normaliseLabelOverride(text))
          }
          onActivate={() => setNullTarget(RANGE_NULL_TARGET_NO_VALUE)}
        />
        {rows.map((row, index) => (
          <RangeGroupingRow
            key={row.id}
            index={index}
            value={row.text}
            valueType={valueType}
            valuePrefix={valuePrefix}
            valueSuffix={valueSuffix}
            errorText={errors[index] ? errorTexts[errors[index]] : null}
            isLast={index === rows.length - 1}
            labelText={rowLabels[index]}
            defaultLabel={defaultLabels[index]}
            labelOverride={row.labelOverride}
            isDuplicate={duplicates[index]}
            showLabelLine={rows.length > 1 || rowLabels[index] !== ""}
            autoFocus={row.id === focusId}
            canInsert={canInsertDialogRow(rows)}
            onChange={handleChange}
            onDelete={handleDelete}
            onInsert={handleInsert}
            onLabelChange={handleLabelChange}
            nullTarget={rowNullTarget(index, rows.length, valueType, row.id)}
            isNullTarget={isNullTargetRow(
              index,
              rows.length,
              nullTarget,
              valueType,
              row.id
            )}
            onNullTargetClick={setNullTarget}
            onValueTab={handleValueTab}
          />
        ))}
        {isString && <RangeGroupingMatchHelp />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} isDisabled={!canSave}>
          {translateLabels(labels.RANGE_GROUPING_DIALOG_SAVE, {
            default: "Save",
          })}
        </Button>
        <Button onClick={handleClear} color={"inherit"}>
          {translateLabels(labels.RANGE_GROUPING_DIALOG_CLEAR, {
            default: "Clear",
          })}
        </Button>
        <Button onClick={onClose} color={"inherit"}>
          {translateLabels(labels.RANGE_GROUPING_DIALOG_CANCEL, {
            default: "Cancel",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RangeGroupingDialog;
